import { Component, inject, input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { ActionSheetController, IonButton, IonIcon, LoadingController } from '@ionic/angular/standalone';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ClickStopPropagationDirective } from '@okcargo-apps/ng-utils';
import { EStopType, IFreightBasicForCarrier, IFreightBasicForShipper } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { Observable, Subscription } from 'rxjs';
import { AccountService } from '../../account/account.service';
import { DownloadDocumentService } from '../../download-document/download-document.service';
import { FreightDocumentationService } from '../freight-documentation.service';

@Component({
  selector: 'lib-freight-documentation-links',
  templateUrl: './freight-documentation-links.component.html',
  styleUrls: ['./freight-documentation-links.component.scss'],
  imports: [IonIcon, IonButton, ClickStopPropagationDirective, TranslateModule],
})
export class FreightDocumentationLinksComponent implements OnInit, OnChanges {
  private readonly loadingCtrl = inject(LoadingController);
  private readonly actionSheetCtrl = inject(ActionSheetController);
  private readonly translate = inject(TranslateService);
  private readonly downloadDocumentService = inject(DownloadDocumentService);
  private readonly freightDocumentationService = inject(FreightDocumentationService);
  private readonly accountService = inject(AccountService);

  // TODO: in the future, refactor this component and use diferent services in diferent components for carrier and shipper
  freight = input.required<IFreightBasicForCarrier | IFreightBasicForShipper>();

  private viewPrices = false;

  private readonly subscription: Subscription;

  public readonly userCapabilities$: Observable<ECapabilities[]> = this.accountService.userCapabilities$;
  buttonColor = '';

  constructor() {
    this.subscription = this.userCapabilities$.subscribe((capabilities) => {
      // console.log('Capabilities', capabilities);
      this.viewPrices = capabilities.includes(ECapabilities.VIEW_PRICES);
    });
  }

  ngOnInit(): void {
    this.updateButtonColor();
    this.subscription.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['freight']) {
      this.updateButtonColor();
    }
  }

  private updateButtonColor(): void {
    const freight = this.freight();
    switch (freight.nextStopType) {
      case EStopType.LOAD:
        this.buttonColor = 'success';
        break;
      case EStopType.UNLOAD_LOAD:
        this.buttonColor = 'tertiary';
        break;
      default:
        this.buttonColor = 'primary';
    }
  }

  public async openLoadingOrder(
    freight: IFreightBasicForCarrier | IFreightBasicForShipper,
    viewPrices: boolean,
  ): Promise<void> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.downloading_file'),
    });

    loading.present();
    await this.downloadDocumentService.downloadFile(
      this.freightDocumentationService.getLoadingOrderUrl(freight, viewPrices),
      'loadingOrder' + freight.reference,
    );

    await loading.dismiss();
  }

  public async openECmr(freight: IFreightBasicForCarrier | IFreightBasicForShipper): Promise<void> {
    const loading = await this.loadingCtrl.create({
      message: this.translate.instant('general.downloading_file'),
    });
    loading.present();

    await this.downloadDocumentService.downloadFile(
      this.freightDocumentationService.getECmrUrl(freight),
      'eCMR' + freight.reference,
    );
    await loading.dismiss();
  }

  // TO-DO @oalvarez ¿check isOpen or use actionSheetController (actionSheetController)?
  public async viewDocs(): Promise<void> {
    const actionSheet = await this.actionSheetCtrl.create({
      header: 'Descarga de documentos',
      buttons: [
        {
          text: this.translate.instant('files.ecmr'),
          data: {
            action: 'openECmr',
          },
          handler: async () => {
            await actionSheet.dismiss();
            this.openECmr(this.freight());
          },
        },
        {
          text: this.translate.instant('files.loading_order'),
          data: {
            action: 'openLoadingOrder',
          },
          handler: async () => {
            await actionSheet.dismiss();
            this.openLoadingOrder(this.freight(), this.viewPrices);
          },
        },
        {
          text: this.translate.instant('files.cancel'),
          role: 'cancel',
        },
      ],
    });

    await actionSheet.present();
  }
}
